import { useContext, useEffect, useState, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { BEM_NAME_ELEMENTS, STATUS_TYPES } from '../../_const';
import NavCard from '../../components/NavCard';
import NavRow from 'pages/AnnualUserAccessReview/components/NavRow';
import { UserContext } from 'context/User/UserContext';
import {
	fetchBasicDelegatedStaffList,
	fetchBasicStaffList,
} from 'pages/AnnualUserAccessReview/_api';
import { processStaffDataForTable } from 'pages/AnnualUserAccessReview/_util';
import WithLoadingHandler from 'hoc/withLoadingHandler/withLoadingHandler';
import ReviewCard from './ReviewCard';
import RenewCard from './RenewCard';
import EditUsersCard from './EditUsersCard';
import TerminateCard from './TerminateCard';
import ReassignCard from './ReassignCard';
import DelegateCard from './DelegateCard';
import * as uarActions from 'store/uar/actions';

/**
 * Navigation directory for User Access Review pages
 * @returns {ReactElement} User Access Review Employee Navigation Directory
 */
const NavigationView = () => {
	const dispatch = useDispatch();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;
	const history = useHistory();
	const [sEmployeeReviewMessages, setEmployeeReviewMessages] = useState([]);
	const [sNonEmployeeReviewMessages, setNonEmployeeReviewMessages] = useState(
		[],
	);
	const [sStaffData, setStaffData] = useState([]);
	const [sLoading, setLoading] = useState(true);

	useEffect(() => {
		let active = true;

		// Clear any active annual review session state on entry to UAR home
		dispatch(uarActions.clearActionCart());
		dispatch(uarActions.clearProcessedRequests());
		dispatch(uarActions.resetQueue());

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchBasicStaffList(user.ID);
			const response2 = await fetchBasicDelegatedStaffList(user.ID);
			const allValues = [...response.values, ...response2.values];
			const data = processStaffDataForTable(allValues);
			setStaffData(data);
			//set the state - employees
			const employeeMessages = [];
			const employeeOverdue = data.filter(
				d =>
					d.status === STATUS_TYPES.OVERDUE &&
					d.accountCategoryName === 'EMPLOYEE',
			);
			const employeeToBeReviewed = data.filter(
				d =>
					d.status === STATUS_TYPES.VALID_EXPIRING_SOON &&
					d.accountCategoryName === 'EMPLOYEE',
			);
			const employeeDelegated = data.filter(
				d => !!d.delegatedById && d.accountCategoryName === 'EMPLOYEE',
			);

			const employeeDelegatedOverdue = employeeDelegated.filter(
				d => d.status === STATUS_TYPES.OVERDUE,
			);

			if (employeeDelegated.length > 0) {
				employeeMessages.push({
					type: 'info',
					message: `There are ${employeeDelegated.length} employee(s) whose reviews have been delegated to you. ${employeeDelegatedOverdue.length} need reviewed.`,
				});
			}
			if (employeeOverdue.length > 0) {
				employeeMessages.push({
					type: 'error',
					message: `There are ${employeeOverdue.length} employee(s) whose reviews are overdue. Please perform their reviews as soon as possible.`,
				});
			}
			if (employeeToBeReviewed.length > 0) {
				employeeMessages.push({
					type: 'warning',
					message: `There are ${employeeToBeReviewed.length} employee(s) whose reviews will expire within the next 30 days. Please perform their reviews soon.`,
				});
			}

			if (
				employeeOverdue.length === 0 &&
				employeeToBeReviewed.length === 0
			) {
				employeeMessages.push({
					type: 'info',
					message: `All employees have been reviewed and none will expire within the next 30 days.`,
				});
			}

			setEmployeeReviewMessages(employeeMessages);

			//set the state - non-employees
			const nonEmployeeMessages = [];
			const nonEmployeeOverdue = data.filter(
				d =>
					d.status === STATUS_TYPES.OVERDUE &&
					d.accountCategoryName === 'NON_EMPLOYEE',
			);
			const nonEmployeeToBeReviewed = data.filter(
				d =>
					d.status === STATUS_TYPES.VALID_EXPIRING_SOON &&
					d.accountCategoryName === 'NON_EMPLOYEE',
			);
			const nonEmployeeDelegated = data.filter(
				d =>
					!!d.delegatedById &&
					d.accountCategoryName === 'NON_EMPLOYEE',
			);

			if (nonEmployeeDelegated.length > 0) {
				nonEmployeeMessages.push({
					type: 'info',
					message: `There are ${employeeOverdue.length} employee(s) whose reviews have been delegated to you.`,
				});
			}

			if (nonEmployeeOverdue.length > 0) {
				nonEmployeeMessages.push({
					type: 'error',
					message: `There are ${nonEmployeeOverdue.length} non-employee(s) whose reviews are overdue. Please perform their reviews as soon as possible.`,
				});
			}
			if (nonEmployeeToBeReviewed.length > 0) {
				nonEmployeeMessages.push({
					type: 'warning',
					message: `There are ${nonEmployeeToBeReviewed.length} non-employee(s) whose reviews will expire within the next 30 days. Please perform their reviews soon.`,
				});
			}

			if (
				nonEmployeeOverdue.length === 0 &&
				nonEmployeeToBeReviewed.length === 0
			) {
				nonEmployeeMessages.push({
					type: 'info',
					message: `All non-employees have been reviewed and none will expire within the next 30 days.`,
				});
			}

			setNonEmployeeReviewMessages(nonEmployeeMessages);
			setLoading(false);
		}

		if (active && user.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [dispatch, user]);

	return (
		<>
			<Typography
				variant='h6'
				color='primary'
				align='center'
				gutterBottom
			>
				User Access Review
			</Typography>
			<WithLoadingHandler isLoading={sLoading} showChildren={!sLoading}>
				{sStaffData?.length > 0 ? (
					<>
						<NavRow>
							<ReviewCard
								staffData={sStaffData.filter(
									s => s.accountCategoryName === 'EMPLOYEE',
								)}
								messages={sEmployeeReviewMessages}
							/>
							<RenewCard
								staffData={sStaffData.filter(
									s =>
										s.accountCategoryName ===
										'NON_EMPLOYEE',
								)}
								messages={sNonEmployeeReviewMessages}
							/>
						</NavRow>
						<NavRow>
							<EditUsersCard
								staffData={sStaffData.filter(
									s => s.isDelegated === false,
								)}
								user={user}
							/>
							<TerminateCard
								staffData={sStaffData.filter(
									s => s.isDelegated === false,
								)}
							/>
							<ReassignCard
								staffData={sStaffData.filter(
									s =>
										s.accountCategoryName ===
											'NON_EMPLOYEE' &&
										s.isDelegated === false,
								)}
							/>
							<DelegateCard
								staffData={sStaffData.filter(
									s =>
										s.accountCategoryName === 'EMPLOYEE' &&
										s.isDelegated === false,
								)}
							/>
						</NavRow>
					</>
				) : (
					<NavRow>
						<NavCard
							title={'Annual User Access Review'}
							icon={<GppMaybeIcon color='error' />}
							role={''}
							description={''}
							onClick={() => history.push('/index.html')}
							messages={[
								{
									type: 'warning',
									message: `You are not registered as a manager and/or you do not have any staff reporting to you.`,
								},
							]}
						/>
					</NavRow>
				)}
			</WithLoadingHandler>
		</>
	);
};

export default NavigationView;
